import React, { useState } from "react";
import { Grid3x3, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel } from "@/pages/shared";
import { gridPositions, MAP_RATIO } from "@/lib/grid";
import { SIZE_PCT } from "@/components/dashboard/CampsiteMap";

const KINDS = ["pitch", "bungalow", "cabin"];
const COLORS = ["#22c55e", "#6366f1", "#3b82f6", "#14b8a6", "#eab308", "#ef4444", "#f97316", "#84cc16", "#0ea5e9", "#a855f7"];

export default function ZonesPanel({ zones, pitches = [], onReload }) {
  const { t } = useLang();
  const [form, setForm] = useState({ code: "", name: "", kind: "pitch", color: COLORS[0] });
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/zones", form);
      setForm({ code: "", name: "", kind: "pitch", color: COLORS[0] });
      await onReload();
      toast.success(t("zone_created"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Alle nog niet geplaatste plaatsen van een zone in een raster zetten, startend bij de laatst gekende positie van die zone
  const gridZone = async (code) => {
    const inZone = pitches.filter((p) => p.zone === code);
    const todo = inZone.filter((p) => p.x == null).sort((a, b) => a.number - b.number);
    if (!todo.length) { toast.info(t("unplaced_empty")); return; }
    const anchors = inZone.filter((p) => p.x != null).sort((a, b) => b.number - a.number);
    const pct = SIZE_PCT[todo[0].size] || SIZE_PCT[1];
    const stepY = (pct * 100 + 0.4) * MAP_RATIO;
    const x0 = anchors.length ? anchors[0].x : 50;
    const y0 = anchors.length ? Math.min(97, anchors[0].y + stepY) : 50;
    const spots = gridPositions(x0, y0, todo.length, pct, 6);
    try {
      await api.post("/pitches/positions", { items: todo.map((p, i) => ({ id: p.id, ...spots[i] })) });
      await onReload();
      toast.success(t("placed_n").replace("{n}", todo.length));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async (code) => {
    try { await api.delete(`/zones/${code}`); await onReload(); toast.success(t("deleted")); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Panel title={t("zones")} testid="zones-panel">
      <div className="p-5 space-y-4">
        <p className="text-sm text-muted-foreground">{t("zones_desc")}</p>
        <div className="flex flex-wrap gap-2">
          {zones.map((z) => (
            <span key={z.code} className="inline-flex items-center gap-2 rounded-full border border-border bg-card pl-2 pr-1 py-1 text-xs font-semibold" data-testid={`zone-chip-${z.code}`}>
              <span className="h-3 w-3 rounded-full" style={{ background: z.color }} />
              {z.code} · {z.name}
              <span className="text-muted-foreground font-normal">({z.counts?.total ?? 0})</span>
              <button onClick={() => gridZone(z.code)} className="p-1 rounded-full hover:bg-secondary" title={t("grid_place")} data-testid={`grid-zone-${z.code}`}>
                <Grid3x3 className="h-3.5 w-3.5 text-primary" />
              </button>
              <button onClick={() => remove(z.code)} className="p-1 rounded-full hover:bg-rose-50" title={t("delete")} data-testid={`delete-zone-${z.code}`}>
                <Trash2 className="h-3.5 w-3.5 text-rose-500" />
              </button>
            </span>
          ))}
        </div>
        <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="mb-1.5 block text-xs">{t("zone_code")}</Label>
            <Input required value={form.code} maxLength={4} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="new-zone-code" />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">{t("zone_name")}</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-zone-name" />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">{t("type")}</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="new-zone-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{t(`kind_${k}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">{t("color")}</Label>
            <div className="flex flex-wrap gap-1">
              {COLORS.map((c) => (
                <button type="button" key={c} onClick={() => setForm({ ...form, color: c })} data-testid={`zone-color-${c.slice(1)}`}
                  className={`h-5 w-5 rounded-full border-2 ${form.color === c ? "border-primary scale-110" : "border-white"} shadow-sm transition-transform`} style={{ background: c }} />
              ))}
            </div>
          </div>
          <Button type="submit" disabled={busy || !form.code || !form.name} data-testid="new-zone-submit"><Plus className="h-4 w-4 mr-1" />{t("new_zone")}</Button>
        </form>
      </div>
    </Panel>
  );
}
